import * as React from 'react';
import { useMemo } from 'react';
import { BuildingOfficeIcon, UserGroupIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { cn, getAssetSrc } from '@restworld/utils-common';

import { Card, CardTypes } from '../card';
import { Heading } from '@restworld/ui-ds-base';

const RestaurantStatistics = ({
  covers,
  staffSize,
  variant = 'horizontal',
}: {
  covers?: string;
  staffSize?: string;
  variant?: 'horizontal' | 'vertical';
}) => {
  const { t } = useTranslation('common');
  const statisticsEl = useMemo(
    () =>
      [
        ...(covers
          ? [
              {
                key: 'covers-number',
                content: covers,
                icon: 'SpoonPlateIcon',
                iconTitle: t('covered'),
                // content: `${coversNumber ? `${coversNumber}/80` : '-'}`,
              },
            ]
          : []),
        ...(staffSize
          ? [
              {
                key: 'staff-size',
                content: staffSize,
                icon: 'UserGroupIcon',
                iconTitle: 'Staff',
              },
            ]
          : []),
      ].map(({ key, content, icon, iconTitle }) => {
        return variant === 'vertical' ? (
          <RestaurantStatisticsCardVariantTwo
            key={key}
            content={content}
            icon={icon as RestaurantStatisticsCardProps['icon']}
            iconTitle={iconTitle}
          />
        ) : (
          <RestaurantStatisticsCardVariantOne
            key={key}
            content={content}
            icon={icon as RestaurantStatisticsCardProps['icon']}
            iconTitle={iconTitle}
            cardVariant="gray100"
            contentStyling={'text-gray-500 font-semibold text-2xl leading-9'}
            titleStyling={'text-gray-500 font-normal text-base'}
            noShadow={true}
          />
        );
      }),
    [covers, staffSize, t, variant]
  );
  return variant === 'vertical' ? (
    <div className="flex flex-row gap-4 items-center">{statisticsEl}</div>
  ) : (
    <div className="flex flex-row gap-2 w-full">{statisticsEl}</div>
  );
};
export interface RestaurantStatisticsCardProps {
  cardVariant?: CardTypes['variant'];
  titleStyling?: string;
  contentStyling?: string;
  noShadow?: boolean;
  cardCentered?: boolean;
  content: string;
  icon: 'SpoonPlateIcon' | 'UserGroupIcon' | 'BuildingOfficeIcon' | 'BriefcasePurple800Icon';
  iconTitle: string;
}
const RestaurantStatisticsCardVariantOne = ({
  content,
  icon,
  iconTitle,
  cardVariant = 'primary50',
  cardCentered = false,
  titleStyling,
  contentStyling,
  noShadow,
}: RestaurantStatisticsCardProps) => {
  return (
    <CardWrapper>
      <Card
        variant={cardVariant}
        twStyle={cardCentered ? 'min-h-fit' : 'items-center gap-2 min-h-fit'}
        noShadow={noShadow}
      >
        <Heading as="span" size={4} color="primary" className={contentStyling}>
          {content}
        </Heading>
        <Heading as="span" size={5} color="muted" className={titleStyling}>
          {iconTitle}
        </Heading>
      </Card>
    </CardWrapper>
  );
};
const RestaurantStatisticsCardVariantTwo = ({
  content,
  icon,
  iconTitle,
  titleStyling = 'leading-4',
  contentStyling = '',
}: RestaurantStatisticsCardProps) => {
  return (
    <div className="flex flex-row gap-2">
      <div className="w-10 h-10 p-2 bg-primary-50 rounded-lg justify-start items-start gap-2.5 inline-flex">
        {
          {
            SpoonPlateIcon: <img src={getAssetSrc('/utils-assets/spoon-plate.svg')} className="h-6 w-6" alt="" />,
            UserGroupIcon: <UserGroupIcon className="h-6 w-6 text-gray-600" />,
            BuildingOfficeIcon: <BuildingOfficeIcon className="h-6 w-6 text-primary-800" />,
            BriefcasePurple800Icon: <BriefcaseIcon className="h-6 w-6 text-primary-800" />,
          }[icon]
        }
      </div>
      <div className="flex flex-col items-start">
        <Heading as="span" size={6} weight="medium" className={cn('text-xs text-gray-600', titleStyling)}>
          {iconTitle}
        </Heading>
        <Heading as="span" size={5} weight="semibold" className={contentStyling}>
          {content}
        </Heading>
      </div>
    </div>
  );
};

export default RestaurantStatisticsCardVariantOne;
export { RestaurantStatistics, RestaurantStatisticsCardVariantTwo, RestaurantStatisticsCardVariantOne };
const CardWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex w-full gap-2 items-center shadow-none', className)} {...props} />
);
